/**
 * B2BProductCard Component
 * Modern B2B-style product card with all requested elements:
 * - Colored header with icon
 * - Product title, SKU, origin, certifications
 * - Price range or "Price on Request"
 * - MOQ & Lead Time
 * - Stock status and Grade badges
 * - Star rating
 * - Get Quote button
 */

import React from 'react';
import { Star, MapPin, Package, ShoppingBag } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  B2BProductCardProps,
} from '../types/b2bMarketplace.types';
import {
  GRADE_CONFIG,
  STOCK_STATUS_CONFIG,
} from '../types/b2bMarketplace.types';

/**
 * Render star rating
 */
const StarRating: React.FC<{ rating: number; reviewCount: number }> = ({
  rating,
  reviewCount,
}) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center" aria-label={`Rating: ${rating} out of 5`}>
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={cn(
              'w-3.5 h-3.5',
              i < fullStars
                ? 'text-[#FFC107] fill-[#FFC107]'
                : i === fullStars && hasHalfStar
                ? 'text-[#FFC107] fill-[#FFC107]/50'
                : 'text-gray-300'
            )}
          />
        ))}
      </div>
      <span className="text-gray-500 text-[13px]">({reviewCount})</span>
    </div>
  );
};

/**
 * B2BProductCard Component
 */
export const B2BProductCard: React.FC<B2BProductCardProps> = ({
  product,
  onGetQuote,
  className,
}) => {
  const gradeConfig = GRADE_CONFIG[product.grade];
  const stockConfig = STOCK_STATUS_CONFIG[product.stockStatus];

  const handleGetQuote = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onGetQuote) {
      onGetQuote(product);
    }
  };

  const formatPrice = () => {
    if (product.priceOnRequest) {
      return (
        <>
          <span className="text-[#1B6B3C] font-bold text-[22px]">Price on Request</span>
        </>
      );
    }
    if (product.priceMin === product.priceMax) {
      return (
        <>
          <span className="text-[#1B6B3C] font-bold text-[22px]">
            ₹{product.priceMin.toFixed(2)}
          </span>
          <span className="text-gray-500 text-sm">
            INR 1 / {product.unit}
          </span>
        </>
      );
    }
    return (
      <>
        <span className="text-[#1B6B3C] font-bold text-[22px]">
          ₹{product.priceMin.toFixed(2)}
        </span>
        <span className="text-gray-500 text-sm">
          INR 1 / {product.unit}
        </span>
      </>
    );
  };

  return (
    <div
      className={cn(
        'bg-[#E8F5E9] rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-all duration-200 hover:-translate-y-1 relative',
        className
      )}
    >
      {/* Best Sell Badge (using Grade badge) */}
      <div className="absolute top-4 left-4 bg-[#FF8C00] text-white text-[11px] font-semibold px-2.5 py-1 rounded z-10">
        {gradeConfig.label}
      </div>

      {/* Product Image */}
      <div className="flex items-center justify-center h-44 mb-4 text-7xl">
        {product.icon || '📦'}
      </div>

      {/* Product Info */}
      <div className="space-y-2">
        {/* Title */}
        <h3 className="text-[#2C3E50] font-semibold text-base leading-tight">
          {product.name && product.name.trim() ? product.name : 'Untitled Product'}
        </h3>

        {/* Location */}
        <div className="flex items-center gap-1.5 text-gray-600 text-[13px]">
          <MapPin className="w-3.5 h-3.5" />
          <span className="truncate">{product.processingUnit.name}</span>
        </div>

        {/* COD Available / SKU */}
        <div className="flex items-center gap-1.5 text-gray-600 text-[13px]">
          <Package className="w-3.5 h-3.5" />
          <span>SKU: {product.sku}</span>
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-1.5 pt-1">
          {formatPrice()}
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1.5">
          <StarRating rating={product.rating} reviewCount={product.reviewCount} />
        </div>

        {/* Tags */}
        <div className="flex items-center gap-2 pt-1">
          <span className="bg-[#D4EDDA] text-[#1B6B3C] text-[11px] font-medium px-3 py-1 rounded-full">
            {gradeConfig.label.toUpperCase()}
          </span>
          <span className="bg-[#D4EDDA] text-[#1B6B3C] text-[11px] font-medium px-3 py-1 rounded-full">
            {stockConfig.label.toUpperCase()}
          </span>
        </div>

        {/* MOQ & Lead Time (Quantity Selector equivalent) */}
        <div className="flex items-center gap-2 pt-2">
          <div className="flex-1 px-3 py-1.5 text-[13px] font-medium rounded-md border bg-white text-gray-700 border-gray-300 text-center">
            MOQ: {product.moq} {product.moqUnit}
          </div>
          <div className="flex-1 px-3 py-1.5 text-[13px] font-medium rounded-md border bg-white text-gray-700 border-gray-300 text-center">
            {product.leadTimeDays.min}–{product.leadTimeDays.max} days
          </div>
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleGetQuote}
          className="w-full bg-[#1B6B3C] hover:bg-[#155A31] text-white font-semibold py-3 rounded-md transition-all flex items-center justify-center gap-2 mt-3"
        >
          <ShoppingBag className="w-4 h-4" />
          {product.priceOnRequest ? 'SEND INQUIRY' : 'GET QUOTE'}
        </button>
      </div>
    </div>
  );
};

export default B2BProductCard;
